import {
    type IncomeSplitConfig,
    PaycheckRoutingStatus,
} from '@fruitful/income-split';
import { AnalyticsUserEvent } from '@fruitful/types';
import { useAtom } from 'jotai';
import React, { useEffect, useRef, useState } from 'react';
import {
    BodyMediumRegular,
    Button,
    ButtonSize,
    ButtonVariant,
    LabelRegular,
    Sheet,
    Spinner,
    YStack,
} from 'smoothie';

import { captureMessage, track } from '../../../logger/Logger';
import { incomeSplitAtom } from '../../state/PaycheckRoutingAtoms';
import {
    FRUITFUL_FDIC_DISCLOSURE,
    INVESTMENT_PRODUCTS_FDIC_DISCLOSURE,
} from '../Disclosures';
import { IncomeSplitAgreement } from './IncomeSplitReviewNew';

interface SheetProps {
    isOpen: boolean;
    isUpdating: boolean;
    setIsUpdating: (isUpdating: boolean) => void;
    onSave: (status: PaycheckRoutingStatus) => Promise<void>;
    onDismiss: () => void;
}

const EnableSheet = ({
    isOpen,
    isUpdating,
    setIsUpdating,
    onSave,
    onDismiss,
}: SheetProps) => {
    const [hasAcceptedAgreement, setHasAcceptedAgreement] = useState(false);

    return (
        <Sheet
            isOpen={isOpen}
            onClose={onDismiss}
            dismissOnOverlayPress={!isUpdating}
            title="You are turning on your Income Split"
            showCloseButton={false}
        >
            <YStack
                paddingTop={8}
                width="100%"
                maxWidth={500}
                alignSelf="center"
                gap={24}
            >
                <YStack gap={4}>
                    <BodyMediumRegular color="$gray1000">
                        Starting with your next deposit, transfers will process
                        from top to bottom subject to available funds.
                    </BodyMediumRegular>
                </YStack>

                <IncomeSplitAgreement
                    id="combined-accept"
                    checked={hasAcceptedAgreement}
                    setChecked={(checked) => {
                        setHasAcceptedAgreement(checked);
                    }}
                />

                <YStack gap={12}>
                    <Button
                        variant={ButtonVariant.PRIMARY}
                        size={ButtonSize.LARGE}
                        text={isUpdating ? '' : 'Turn on Income Split'}
                        icon={isUpdating ? <Spinner /> : null}
                        onPress={async () => {
                            setIsUpdating(true);
                            track(AnalyticsUserEvent.INCOME_SPLIT_ENABLED);
                            captureMessage('Income Split enabled');
                            await onSave(PaycheckRoutingStatus.ACTIVE);
                            setIsUpdating(false);
                        }}
                        disabled={!hasAcceptedAgreement || isUpdating}
                    />
                    <Button
                        variant={ButtonVariant.TERTIARY}
                        size={ButtonSize.LARGE}
                        text="Cancel"
                        onPress={onDismiss}
                        disabled={isUpdating}
                    />
                    <LabelRegular color="$gray900" marginTop={4}>
                        {FRUITFUL_FDIC_DISCLOSURE}
                        {'\n\n'}
                        {INVESTMENT_PRODUCTS_FDIC_DISCLOSURE}
                    </LabelRegular>
                </YStack>
            </YStack>
        </Sheet>
    );
};

const DisableSheet = ({
    isOpen,
    isUpdating,
    setIsUpdating,
    onSave,
    onDismiss,
}: SheetProps) => {
    return (
        <Sheet
            isOpen={isOpen}
            onClose={onDismiss}
            dismissOnOverlayPress={!isUpdating}
            title="Are you sure you want to turn off your Income Split?"
            showCloseButton={false}
        >
            <YStack
                paddingTop={8}
                width="100%"
                maxWidth={500}
                alignSelf="center"
                gap={24}
            >
                <YStack gap={8}>
                    <BodyMediumRegular color="$gray1000">
                        This change will take effect immediately. Any transfers
                        using these Income Split rules already in progress will
                        complete, but no new transfers will be initiated.
                    </BodyMediumRegular>
                </YStack>

                <YStack gap={12}>
                    <Button
                        variant={ButtonVariant.PRIMARY}
                        tone="danger"
                        size={ButtonSize.LARGE}
                        text={isUpdating ? '' : 'Yes, turn off Income Split'}
                        icon={isUpdating ? <Spinner /> : null}
                        onPress={async () => {
                            setIsUpdating(true);
                            track(AnalyticsUserEvent.INCOME_SPLIT_DISABLED);
                            captureMessage('Income Split disabled');
                            await onSave(PaycheckRoutingStatus.INACTIVE);
                            setIsUpdating(false);
                        }}
                        disabled={isUpdating}
                    />
                    <Button
                        variant={ButtonVariant.TERTIARY}
                        size={ButtonSize.LARGE}
                        text="Cancel"
                        onPress={onDismiss}
                        disabled={isUpdating}
                    />
                </YStack>
            </YStack>
        </Sheet>
    );
};

interface IncomeSplitToggleSheetProps {
    isOpen: boolean;
    onSave: (status: PaycheckRoutingStatus) => Promise<void>;
    /** Called when the sheet closes after a successful save. No revert needed. */
    onClose: () => void;
    /** Called when the sheet closes without a completed save (Cancel, backdrop, swipe). Reverts the optimistic toggle. */
    onCancel: () => void;
}

const IncomeSplitToggleSheet = ({
    isOpen,
    onSave,
    onClose,
    onCancel,
}: IncomeSplitToggleSheetProps) => {
    const [incomeSplit, setIncomeSplit] = useAtom(incomeSplitAtom);
    const [isUpdating, setIsUpdating] = useState(false);
    // Tracks whether the in-flight save has completed successfully, so a close
    // triggered by that success (or any dismiss racing with it) doesn't also
    // revert the just-confirmed status back to its pre-toggle value.
    const hasSavedRef = useRef(false);

    useEffect(() => {
        if (isOpen) {
            hasSavedRef.current = false;
        }
    }, [isOpen]);

    // the status has already been temporarily changed by the switch
    const action =
        incomeSplit?.status === PaycheckRoutingStatus.ACTIVE
            ? 'enable'
            : 'disable';

    const handleDismiss = () => {
        if (hasSavedRef.current) {
            onClose();
            return;
        }

        setIncomeSplit({
            ...(incomeSplit as IncomeSplitConfig),
            status:
                action === 'enable'
                    ? PaycheckRoutingStatus.INACTIVE
                    : PaycheckRoutingStatus.ACTIVE,
        });
        onCancel();
    };

    const EnableDisableSheet = action === 'enable' ? EnableSheet : DisableSheet;

    return (
        <EnableDisableSheet
            isUpdating={isUpdating}
            isOpen={isOpen}
            setIsUpdating={setIsUpdating}
            onSave={async (status: PaycheckRoutingStatus) => {
                hasSavedRef.current = true;
                try {
                    await onSave(status);
                } catch (error) {
                    hasSavedRef.current = false;
                    throw error;
                }
            }}
            onDismiss={handleDismiss}
        />
    );
};

export default IncomeSplitToggleSheet;
